import {
  Box,
  BoxHeader,
  BoxSection,
  Button,
  CollapsibleContainer,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  StatusBadge,
} from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import {
  addDataRequestNote,
  updateDataRequest as apiUpdateDataRequest,
  deleteDataRequestNote,
} from '~/api/civilsearch';
import { type BillingTier, billingTierLabels } from '~/common/dataRequestUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import {
  type DataRequestNote,
  type DataRequestV2,
  type Outcome,
  RequestStatus,
  type UtilityPresence,
} from '~/common/types';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import Input from '~/components2/forms/Input';
import TextArea from '~/components2/forms/TextArea';
import Icon from '~/components2/Icon';
import InfoTooltip from '~/components2/InfoTooltip';
import { Notify } from '~/components2/Notify';
import { statusConfig } from '~/pages/admin/civilsearch/common';
import DiscoverySourceBadges from '~/pages/admin/civilsearch/common/DiscoverySourceBadges';
import FormSection from '~/pages/admin/civilsearch/common/FormSection';
import { renderRequestStatus } from '~/pages/projectDetails/utilitiesV2/Table/columns';
import { deriveRequestScope, regionPositionLabel, scopedDataCaption } from '../../requestScope';
import store from '../../store';
import AdminSelect from './AdminSelect';
import FollowUpPanel from './FollowUpPanel';
import MissiveEmailHelpers from './MissiveEmailHelpers';

export type TFormValues = {
  requestStatus: RequestStatus;
  utilityPresence?: UtilityPresence;
  outcome?: Outcome;
  notes: string;
  notesInternal: string;
  status: 'active' | 'archived';
  assignedTo: string;
  createdAt: DateTime;
  firstContactedAt?: DateTime;
  secondContactedAt?: DateTime;
  thirdContactedAt?: DateTime;
  fourthContactedAt?: DateTime;
  resolutionDate?: DateTime;
  calledDate?: DateTime;
  nextFollowUpAt?: DateTime | null;
};

interface Props {
  dataRequest: DataRequestV2;
  refetch: () => void;
}

function formatNoteDate(iso: string) {
  const dt = DateTime.fromISO(iso);
  return dt.toFormat(`MMMM d, yyyy, h:mm`) + dt.toFormat('a');
}

export default function GeneralForm({ dataRequest, refetch }: Props) {
  const { project } = useSnapshot(store) as typeof store;
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<DataRequestNote | null>(null);
  const [deletingNote, setDeletingNote] = useState(false);

  // CG-474: fire once per request view; is_sub_geometry_scoped tells the data team
  // whether this request was routed to a specific sub-geometry.
  useEffect(() => {
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchRequestDetailsViewed, {
      request_id: dataRequest.id,
      is_sub_geometry_scoped: Boolean(dataRequest.agencySubGeometryId),
    });
  }, [dataRequest.id, dataRequest.agencySubGeometryId]);

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;
    setDeletingNote(true);
    try {
      await deleteDataRequestNote(dataRequest.id, noteToDelete.id);
      setNoteToDelete(null);
      refetch();
    } catch {
      Notify.error({ message: 'Failed to delete note' });
    } finally {
      setDeletingNote(false);
    }
  };

  const handleAddNote = async () => {
    const trimmed = noteInput.trim();
    if (!trimmed) return;
    setAddingNote(true);
    try {
      await addDataRequestNote(dataRequest.id, trimmed);
      setNoteInput('');
      refetch();
    } catch {
      Notify.error({ message: 'Failed to add note' });
    } finally {
      setAddingNote(false);
    }
  };
  const uniqueId = `[[REF-CG-${project!.projectId}-${dataRequest?.requestId}]]`;

  // CG-749: sub-region identity for the Agency Info section. The sub-header names the
  // sub-region this request is tied to (internal sub-geometry name, falling back to its
  // region number) and the caption says which scope the backend resolved the
  // contacts/links/notes to (always data-driven — see scopedDataCaption).
  const scope = useMemo(() => deriveRequestScope(dataRequest, dataRequest.siblings), [dataRequest]);
  const subGeometryName = dataRequest.subGeometry?.name ?? null;
  let subRegionLabel: string | null = null;
  if (dataRequest.agencySubGeometryId) {
    subRegionLabel = subGeometryName ?? (scope ? regionPositionLabel(scope) : 'Unnamed sub-region');
  } else if (scope?.isCatchAll) {
    subRegionLabel = 'Remaining area (not tied to a specific sub-region)';
  }
  const scopedCaption = scopedDataCaption(dataRequest, scope);

  const form = useForm<TFormValues>({
    defaultValues: {
      requestStatus: dataRequest.requestStatus,
      utilityPresence: dataRequest.utilityPresence,
      outcome: dataRequest.outcome,
      status: dataRequest.status,
      notes: dataRequest.notes || '',
      notesInternal: dataRequest.notesInternal || '',
      assignedTo: dataRequest.assignedTo?.id || '',
      createdAt: dataRequest.createdAt,
      firstContactedAt: dataRequest.firstContactedAt,
      secondContactedAt: dataRequest.secondContactedAt,
      thirdContactedAt: dataRequest.thirdContactedAt,
      fourthContactedAt: dataRequest.fourthContactedAt,
      resolutionDate: dataRequest.resolutionDate,
      calledDate: dataRequest.calledDate,
      nextFollowUpAt: dataRequest.nextFollowUpAt,
    },
  });

  const requestStatus = useWatch({
    control: form.control,
    name: 'requestStatus',
  });

  useEffect(() => {
    const now = DateTime.now();

    const statusToDateFieldMap: Partial<Record<RequestStatus, keyof TFormValues>> = {
      [RequestStatus.InProgress0]: 'firstContactedAt',
      [RequestStatus.InProgress1]: 'secondContactedAt',
      [RequestStatus.InProgress2]: 'thirdContactedAt',
      [RequestStatus.InProgress3]: 'fourthContactedAt',
      [RequestStatus.Complete]: 'resolutionDate',
    };

    const dateField = statusToDateFieldMap[requestStatus];
    if (dateField && !form.getValues(dateField)) {
      form.setValue(dateField, now, { shouldDirty: true });
    }

    if (
      requestStatus === RequestStatus.Complete ||
      requestStatus === RequestStatus.Cancelled ||
      requestStatus === RequestStatus.Dismissed
    ) {
      const currentNextFollowUpAt = form.getValues('nextFollowUpAt');
      if (currentNextFollowUpAt != null) {
        form.setValue('nextFollowUpAt', null, { shouldDirty: true });
      }
    }
  }, [requestStatus, form]);

  const statusItems = useMemo(() => {
    return statusConfig.map(({ value, label }) => ({
      value,
      label: <div className="text-xs">{label}</div>,
      keywords: [label],
    }));
  }, []);

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    if (!form.formState.isDirty) return;

    const properties: { [key: string]: any } = {};

    for (const key in form.formState.dirtyFields) {
      const k = key as keyof TFormValues;
      const v = data[k];
      properties[k] = v;
    }

    if (isEmpty(properties)) return;

    if (
      properties['requestStatus'] === RequestStatus.ActionRequired ||
      properties['requestStatus'] === RequestStatus.OnHoldPerAgency
    ) {
      const effectiveNotes = 'notes' in properties ? properties['notes'] : dataRequest.notes;
      if (isEmpty(effectiveNotes)) {
        alert('Action required or On hold (per agency) required public notes to be present');
        return;
      }
    }

    if (
      properties['requestStatus'] === RequestStatus.Complete &&
      dataRequest.invoices.length > 0 &&
      dataRequest.invoices.some((invoice) => invoice.isRequestFulfilled === false)
    ) {
      alert('There is a pending invoice that is not marked as fulfilled, please update invoice first');
      return;
    }

    // Check to see if a resolution date is set without a complete status.
    const hasResolutionDate = dataRequest.resolutionDate !== null;
    const isComplete = dataRequest.requestStatus === RequestStatus.Complete;

    const settingResolutionDate = 'resolutionDate' in properties && properties['resolutionDate'] !== null;
    const clearingResolutionDate = 'resolutionDate' in properties && properties['resolutionDate'] == null;

    const changingStatus = 'requestStatus' in properties;
    const settingComplete = 'requestStatus' in properties && properties['requestStatus'] === RequestStatus.Complete;

    if (settingResolutionDate && !isComplete && !settingComplete) {
      Notify.error({
        message: 'Cannot set a resolution date on a non-complete data request',
      });
      return;
    }

    if (changingStatus && !settingComplete && hasResolutionDate && !clearingResolutionDate) {
      Notify.error({
        message: 'Cannot change status when data request has resolution date',
      });
      return;
    }

    try {
      await apiUpdateDataRequest(dataRequest!.id, properties);
      Notify.info({ message: 'Data request successfully updated' });

      refetch();
    } catch (e) {
      console.error(e);
      Notify.error({ message: 'An error occurred while updating the request' });
    }
  };

  const formValues = useWatch({ control: form.control });

  const appStatusPreview = useMemo(() => {
    const { color, trigger, tooltip } = renderRequestStatus(
      formValues.requestStatus!,
      formValues.utilityPresence,
      formValues.outcome,
    );
    return (
      <StatusBadge tooltip={tooltip} color={color} data-testid="request-status-preview-badge">
        {trigger}
      </StatusBadge>
    );
  }, [formValues]);

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="flex w-full flex-col justify-between space-y-4">
        <div>
          <Button type="submit" disabled={!form.formState.isDirty}>
            Save Request
          </Button>
        </div>

        <div className="flex w-full space-x-4">
          <div className="w-1/2">
            <FormSection title="General">
              <Input
                label="Unique ID"
                value={uniqueId}
                tooltipText="Format = CG-PROJECT_ID-REQUEST_ID"
                disabled
                canCopy={true}
              />
              <AdminSelect form={form} dataRequest={dataRequest} />
              <Input
                label="Project Type"
                value={billingTierLabels[project?.billingTier as BillingTier] ?? project?.billingTier ?? ''}
                disabled
              />
              <ControlledSelect
                name="status"
                label="Active or Archived"
                control={form.control}
                items={statusItems}
                isRequired
                tooltipText='You can mark the request as "ACTIVE" or "ARCHIVED". Users will only be able to see "ACTIVE" requests. Marking a request as "ARCHIVED" essentially "deletes" it for the user.'
                popoverTriggerClassName="w-full"
              />
            </FormSection>
            <FormSection title="Agency Info">
              {subRegionLabel && (
                <div className="space-y-0.5" data-testid="agency-subregion-header">
                  <div className="text-sm font-semibold">Sub-Region: {subRegionLabel}</div>
                  {scopedCaption && <div className="text-xs text-gray-500">{scopedCaption}</div>}
                </div>
              )}
              <div className="flex flex-row items-center gap-3">
                <span>
                  <Link
                    target="_blank"
                    to="/admin/civil-search/agencies/$agencyId"
                    params={{ agencyId: dataRequest.agency.id }}
                    className="text-blue-50 underline hover:text-blue-60"
                  >
                    {dataRequest.agency.agencyName} - {dataRequest.agency.type}
                  </Link>
                </span>
                <DiscoverySourceBadges agency={dataRequest.agency} />
                <div className="max-w-40">
                  <Input label="" value={dataRequest.agency.agencyId} disabled canCopy={true} />
                </div>
              </div>

              {dataRequest.agency.links?.filter((link) => link.isPrivate).length > 0 && (
                <div className="mb-4">
                  <div className="mb-2 font-medium">Private Files</div>
                  <div className="space-y-1">
                    {dataRequest.agency.links
                      .filter((link) => link.isPrivate)
                      .map((link) => (
                        <div key={link.id} className="flex items-center">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-50 underline hover:text-blue-60"
                          >
                            {link.title}
                          </a>
                          {link.description && <span className="ml-2">- {link.description}</span>}
                        </div>
                      ))}
                  </div>
                </div>
              )}
              {dataRequest.agency.contacts && dataRequest.agency.contacts.length > 0 && (
                <div className="mb-4" data-testid="agency-contacts-section">
                  <div className="mb-2 font-medium">Contacts</div>
                  <div className="space-y-1">
                    {dataRequest.agency.contacts.map((contact) => (
                      <div key={contact.id} className="flex flex-col" data-testid={`agency-contact-row-${contact.id}`}>
                        <span className="font-medium">
                          {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unnamed contact'}
                          {contact.title ? ` — ${contact.title}` : ''}
                        </span>
                        {contact.email && <span className="text-sm">{contact.email}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <TextArea value={dataRequest.agency.notes} canCopy readOnly label={'Internal Agency Notes'} />

                <CollapsibleContainer label={'AI Contact Research'} defaultExpanded={false}>
                  <TextArea
                    value={dataRequest.agency.aiContactResearch}
                    label={'AI Contact Research'}
                    canCopy
                    readOnly
                  />
                </CollapsibleContainer>
              </div>
            </FormSection>
          </div>
          <div className="w-1/2">
            <Box className="max-h-120">
              <BoxHeader className="mb-0">
                <span className="text-base font-semibold text-primary">Internal Notes</span>
              </BoxHeader>

              <BoxSection>
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex gap-3 items-end">
                    <Input
                      label="Add Note"
                      value={noteInput}
                      onChange={(value) => setNoteInput(value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                      data-testid="note-input"
                    />
                    <Button
                      variant="secondary"
                      onClick={handleAddNote}
                      disabled={addingNote || !noteInput.trim()}
                      data-testid="add-note-button"
                    >
                      {addingNote ? 'Adding...' : 'Add Note'}
                    </Button>
                  </div>
                </div>
              </BoxSection>

              <BoxSection className="flex flex-col divide-y p-0 overflow-y-scroll ">
                {dataRequest.requestNotes.map((note) => {
                  return (
                    <div key={note.id} className="text-sm p-3 space-y-2">
                      <div className="flex flex-row justify-between">
                        <div className="space-x-2">
                          <span className="font-semibold">{note.user.name}</span>
                          <span className="text-xs text-secondary">{formatNoteDate(note.createdAt)}</span>
                        </div>
                        <Button
                          variant="secondary"
                          size="icon"
                          type="button"
                          aria-label={`Delete note from ${note.user.name}, ${formatNoteDate(note.createdAt)}`}
                          onClick={() => setNoteToDelete(note)}
                          data-testid={`delete-note-button-${note.id}`}
                        >
                          <Icon icon={'delete'} />
                        </Button>
                      </div>
                      <div className="">{note.note}</div>
                    </div>
                  );
                })}
                {dataRequest.notesInternal && (
                  <div className="text-sm p-3 space-y-2 bg-gray-100">
                    <div className="space-x-2 flex flex-row items-center">
                      <span className="font-semibold">Original Note</span>
                      <InfoTooltip
                        text={'Notes have been updated! The single original note is preserved here'}
                        label="About original note"
                      />
                    </div>
                    <div className="">{dataRequest.notesInternal}</div>
                  </div>
                )}
              </BoxSection>
            </Box>
          </div>
        </div>

        <FormSection title="Contacts + Follow up">
          <MissiveEmailHelpers dataRequest={dataRequest} />
          <div className="flex flex-col gap-1">
            <div className="text-xs">App preview</div>
            <div className="border p-2">{appStatusPreview}</div>
          </div>
          <FollowUpPanel
            control={form.control}
            form={form}
            dataRequest={dataRequest}
            initialRequestStatus={dataRequest.requestStatus}
          />
        </FormSection>

        <FormSection title="Public">
          <ControlledTextArea
            name="notes"
            label="Public Notes"
            control={form.control}
            isRequired={false}
            placeholder="Add PUBLIC notes to the request here..."
            tooltipText="Add PUBLIC notes the the data request. These notes will be visible to the user in the Utilities tab."
          />
        </FormSection>
      </div>

      <Dialog
        open={noteToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setNoteToDelete(null);
        }}
      >
        <DialogContent
          title="Delete Note"
          data-testid="delete-note-dialog"
          closeButtonProps={{ disabled: deletingNote }}
        >
          <DialogDescription className="px-4 space-y-3">
            <div className="space-x-2">
              <span className="font-semibold">{noteToDelete?.user.name}</span>
              <span className="text-xs">{noteToDelete && formatNoteDate(noteToDelete.createdAt)}</span>
            </div>
            <p className="text-sm text-primary">{noteToDelete?.note}</p>
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setNoteToDelete(null)}
              disabled={deletingNote}
              data-testid="delete-note-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteNote}
              disabled={deletingNote}
              data-testid="delete-note-confirm"
            >
              {deletingNote ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
